<template>
  <!-- 파트너 섹션 편집 드로어 -->
  <a-drawer
    :visible="isVisible"
    title="파트너 섹션 편집"
    placement="right"
    :width="drawerWidth"
    :destroy-on-close="false"
    @close="handleClose"
  >
    <div v-if="partnersData" class="partners-editor">
      <!-- 섹션 제목 -->
      <div class="partners-editor__field">
        <label class="partners-editor__label">섹션 제목</label>
        <a-input
          :value="partnersData.title"
          placeholder="섹션 제목을 입력하세요"
          @change="(e) => updateField('title', e.target.value)"
        />
      </div>

      <!-- 로고 목록 -->
      <div class="partners-editor__logos">
        <div class="partners-editor__logos-header">
          <span class="partners-editor__label">파트너 로고 (최대 20개)</span>
          <a-button
            type="dashed"
            size="small"
            :disabled="logoList.length >= 20"
            @click="addLogo"
          >
            + 로고 추가
          </a-button>
        </div>

        <!-- 각 로고 항목 -->
        <div
          v-for="(logo, idx) in logoList"
          :key="idx"
          class="partners-editor__logo-item"
        >
          <div class="partners-editor__logo-header">
            <span class="partners-editor__logo-index">{{ idx + 1 }}</span>
            <a-button
              type="text"
              danger
              size="small"
              @click="removeLogo(idx)"
            >
              삭제
            </a-button>
          </div>

          <!-- 로고 이미지 업로드 -->
          <image-uploader
            :model-value="logo.imageUrl"
            label="로고 이미지"
            :max-size-m-b="2"
            @update:model-value="(url) => updateLogo(idx, 'imageUrl', url)"
          />

          <!-- Alt 텍스트 (필수) -->
          <div class="partners-editor__field" style="margin-top: 8px;">
            <label class="partners-editor__label">Alt 텍스트 <span class="required">*</span></label>
            <a-input
              :value="logo.altText"
              placeholder="이미지 대체 텍스트 (필수)"
              @change="(e) => updateLogo(idx, 'altText', e.target.value)"
            />
          </div>
        </div>

        <!-- 로고 없을 때 안내 -->
        <a-empty
          v-if="logoList.length === 0"
          description="파트너 로고를 추가해주세요"
          :image-style="{ height: '40px' }"
        />
      </div>
    </div>

    <!-- 데이터 미로드 상태 -->
    <div v-else class="partners-editor__empty">
      <a-spin tip="데이터를 불러오는 중..." />
    </div>
  </a-drawer>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import ImageUploader from '../common/ImageUploader.vue';

export default {
  name: 'PartnersEditor',

  components: {
    ImageUploader,
  },

  data() {
    return {
      escHandler: null,
    };
  },

  computed: {
    ...mapState('homepageEditor', ['activeEditor']),
    ...mapGetters('homepageEditor', ['currentPageData']),

    isVisible() {
      return (
        this.activeEditor?.type === 'drawer' &&
        this.activeEditor?.sectionKey === 'partners'
      );
    },

    drawerWidth() {
      return window.innerWidth >= 1440 ? 520 : 420;
    },

    partnersSection() {
      if (!this.currentPageData?.sections) return null;
      return this.currentPageData.sections.find((s) => s.sectionKey === 'partners');
    },

    partnersData() {
      return this.partnersSection?.data || null;
    },

    // 로고 배열 (최대 20개)
    logoList() {
      return this.partnersData?.logos || [];
    },
  },

  watch: {
    isVisible(val) {
      if (val) {
        this.registerEscHandler();
      } else {
        this.removeEscHandler();
      }
    },
  },

  beforeUnmount() {
    this.removeEscHandler();
  },

  methods: {
    handleClose() {
      this.$store.commit('homepageEditor/SET_ACTIVE_EDITOR', null);
    },

    // 섹션 데이터 특정 필드 업데이트
    updateField(field, value) {
      if (!this.partnersSection) return;
      const updatedData = { ...this.partnersData, [field]: value };
      this.$store.commit('homepageEditor/UPDATE_SECTION', {
        page: 'main',
        sectionKey: 'partners',
        data: updatedData,
      });
    },

    // 로고 목록 전체 업데이트
    updateLogoList(logos) {
      this.updateField('logos', logos);
    },

    // 특정 로고 항목 필드 업데이트
    updateLogo(index, field, value) {
      const logos = [...this.logoList];
      logos[index] = { ...logos[index], [field]: value };
      this.updateLogoList(logos);
    },

    // 새 로고 항목 추가
    addLogo() {
      if (this.logoList.length >= 20) return;
      const logos = [...this.logoList, { imageUrl: '', altText: '' }];
      this.updateLogoList(logos);
    },

    // 특정 로고 항목 삭제
    removeLogo(index) {
      const logos = [...this.logoList];
      logos.splice(index, 1);
      this.updateLogoList(logos);
    },

    registerEscHandler() {
      this.escHandler = (e) => {
        if (e.key === 'Escape') this.handleClose();
      };
      window.addEventListener('keydown', this.escHandler);
    },

    removeEscHandler() {
      if (this.escHandler) {
        window.removeEventListener('keydown', this.escHandler);
        this.escHandler = null;
      }
    },
  },
};
</script>

<style scoped>
.partners-editor__field {
  margin-bottom: 16px;
}

.partners-editor__label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

.partners-editor__logos {
  margin-top: 8px;
}

.partners-editor__logos-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.partners-editor__logo-item {
  padding: 14px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  margin-bottom: 12px;
}

.partners-editor__logo-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.partners-editor__logo-index {
  font-size: 12px;
  color: #666;
  font-weight: 600;
}

.partners-editor__empty {
  text-align: center;
  padding: 40px 0;
}

.required {
  color: #ff4d4f;
}
</style>
